// useRowSelection — reusable multi-select state for list pages.
//
// Stores selected row IDs in a SolidJS signal. Resets automatically when the
// `currentIds` source changes (typically because the list query refetched
// after a mutation). `toggle`/`selectAll`/`clear` are stable references the
// caller can pass to checkboxes and action-bar buttons.

import { createEffect, createMemo, createSignal } from "solid-js";

export function useRowSelection<T extends { id: string }>(currentIds: () => string[]) {
  const [selected, setSelected] = createSignal<Set<string>>(new Set());

  // Prune anything that's no longer in the visible list — e.g. after a
  // bulk archive the archived rows fall out, so we mustn't act on them again.
  createEffect(() => {
    const visible = new Set(currentIds());
    setSelected((cur) => {
      const next = new Set<string>();
      for (const id of cur) if (visible.has(id)) next.add(id);
      return next.size === cur.size ? cur : next;
    });
  });

  const has = (id: string) => selected().has(id);

  const toggle = (id: string) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectAll = () => setSelected(new Set<string>(currentIds()));
  const clear = () => setSelected(new Set<string>());

  const count = createMemo(() => selected().size);
  const allSelected = createMemo(() => {
    const n = currentIds().length;
    return n > 0 && n === selected().size;
  });
  const someSelected = createMemo(() => selected().size > 0 && !allSelected());

  // Picks the corresponding row objects out of the most recent list — useful
  // for bulk handlers that need each row's `version` (and other fields).
  function selectedRows(rows: T[]): T[] {
    const sel = selected();
    return rows.filter((r) => sel.has(r.id));
  }

  return {
    selected,
    has,
    toggle,
    selectAll,
    clear,
    count,
    allSelected,
    someSelected,
    selectedRows,
  };
}
